import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { STATUS_STYLE } from "@/data/site";
import { Sparkle } from "@/components/Art";
import { track } from "@/lib/api";

export function StatusBadge({ status }) {
  if (!status) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider ${STATUS_STYLE[status] || ""}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />{status}
    </span>
  );
}

// Spotlit logo plate: venture logos are dark-on-light, so they sit on a lit frame.
export function LogoPlate({ v, className = "", caption = false }) {
  return (
    <div className={`relative grid place-items-center overflow-hidden rounded-2xl bg-[#eef3fa] ${className}`}>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(16,24,40,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(16,24,40,0.05)_1px,transparent_1px)] bg-[size:28px_28px]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,#ffffff_0%,rgba(255,255,255,0.85)_35%,transparent_75%)]" />
      {caption && v.websiteUrl && (
        <span className="absolute bottom-4 left-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#475467]">{v.websiteUrl.replace("https://", "")}</span>
      )}
      {caption && <span className="absolute right-5 top-4 font-mono text-[11px] text-[#475467]">0{v.displayOrder}</span>}
      {v.logo ? (
        <img src={v.logo} alt={`${v.name} logo`} loading="lazy" className="relative max-h-[46%] w-auto max-w-[72%] object-contain" />
      ) : (
        <span className="relative flex items-center gap-3 text-3xl font-extrabold tracking-tight text-si-ink">
          <Sparkle className="h-6 w-6" color="#F98A17" />{v.name}
        </span>
      )}
    </div>
  );
}

// Abstract interface mock that hints at product UI without faking screenshots.
export function InterfaceGlyph({ v }) {
  return (
    <div className="relative h-full w-full overflow-hidden rounded-2xl border border-si-line bg-si-bg/80 p-4" aria-hidden="true">
      <div className="mb-4 flex gap-1.5"><span className="h-2 w-2 rounded-full bg-si-line" /><span className="h-2 w-2 rounded-full bg-si-line" /><span className="h-2 w-2 rounded-full bg-si-line" /></div>
      <div className="grid grid-cols-5 gap-3">
        <div className="col-span-1 space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-2 rounded bg-si-line/80" style={{ width: `${90 - i * 12}%` }} />)}</div>
        <div className="col-span-4 space-y-3">
          <div className="h-16 rounded-lg" style={{ background: `linear-gradient(120deg, ${v.accent}55, ${v.accent2}33)` }} />
          <div className="grid grid-cols-3 gap-2">{[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-md border border-si-line bg-si-surface" />)}</div>
          <div className="flex h-16 items-end gap-1.5">{[40, 65, 50, 80, 60, 95, 75].map((h, i) => <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: i === 5 ? v.accent2 : `${v.accent}88` }} />)}</div>
          <div className="space-y-2 pt-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-2 rounded-md border border-si-line/70 bg-si-surface/60 px-2 py-2">
                <span className="h-2 w-2 rounded-full" style={{ background: i % 3 === 0 ? v.accent2 : v.accent }} />
                <span className="h-1.5 rounded bg-si-line" style={{ width: `${70 - i * 7}%` }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function VentureLinks({ v, compact = false }) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      {v.websiteUrl && v.status === "Live" && (
        <a href={v.websiteUrl} target="_blank" rel="noopener noreferrer" data-testid={`venture-${v.slug}-external`}
          onClick={() => track("external_product_clicked", { venture: v.slug })}
          className="group inline-flex items-center gap-1.5 text-[15px] font-semibold text-si-cyan hover:text-si-text">
          Explore {v.name}<ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      )}
      <Link to={`/ventures/${v.slug}`} data-testid={`venture-${v.slug}-details`}
        onClick={() => track("venture_card_clicked", { venture: v.slug })}
        className="group inline-flex items-center gap-1.5 text-[15px] font-semibold text-si-text/90 hover:text-si-cyan">
        {compact || (v.websiteUrl && v.status === "Live") ? "Details" : `Explore ${v.name}`}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Link>
    </div>
  );
}
